import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { enqueueSnackbar } from 'notistack';
import { Box, Typography, Button } from '@mui/material';
import Message from './Message';

const PendingFormDetails = () => {
  const { id } = useParams();
  const [formDetails, setFormDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isCurrentApprover, setIsCurrentApprover] = useState(false);
  const [isForwarded, setIsForwarded] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const token = localStorage.getItem('token');
  const { role } = jwtDecode(token);

  useEffect(() => {
    const fetchFormDetails = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/active-workflows/${id}`);
        const data = response.data;
        setFormDetails(data);

        const currentApproverNode = data.nodes.find(n => n.id === data.currentApprover);
        setIsCurrentApprover(currentApproverNode?.name === role);

        if (data.status === 'approved') {
          setIsApproved(true);
          setMessage({ text: 'Document already approved', type: 'success' });
        }
        if (data.status === 'rejected') {
          setIsRejected(true);
          setMessage({ text: 'Document already rejected', type: 'error' });
        }
      } catch (error) {
        console.error('Error fetching form details', error);
        enqueueSnackbar('Failed to fetch form details', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchFormDetails();
  }, [id, role]);

  const handleForward = async (newApproverId) => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/active-workflows/forward/${id}`, {
        newApprover: newApproverId,
      });
      setMessage({ text: 'Form forwarded successfully.', type: 'success' });
      setIsCurrentApprover(false);
      setIsForwarded(true);
    } catch (error) {
      console.error('Error forwarding the document', error);
      enqueueSnackbar('Error while forwarding the document', { variant: 'error' });
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/active-workflows/reject/${id}`, {
        rejectedBy: role,
      });
      setMessage({ text: 'Document rejected successfully.', type: 'error' });
      setIsRejected(true);
    } catch (error) {
      console.error('Error rejecting the document', error);
      enqueueSnackbar('Error while rejecting the document', { variant: 'error' });
    }
  };

  const handleApprove = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/active-workflows/approve/${id}`, {
        approvedBy: role,
      });
      setMessage({ text: 'Document approved successfully.', type: 'success' });
      setIsApproved(true);
    } catch (error) {
      console.error('Error approving the document', error);
      enqueueSnackbar('Error while approving the document', { variant: 'error' });
    }
  };

  if (loading) {
    return <Typography variant="body1" align="center" color="textSecondary">Loading form details...</Typography>;
  }

  if (!formDetails || (!isForwarded && !isCurrentApprover)) {
    console.log(!isForwarded && !isCurrentApprover);
    return <Typography variant="body1" align="center" color="error">No pending actions remain for this approval.</Typography>;
  }

  const { formData, finalApprovers, currentApprover, nodes } = formDetails;

  const currentNode = nodes.find(n => n.id === currentApprover);
  const finalApproverNodes = nodes.filter(n => formDetails.finalApprovers.includes(n.id));
  const forwardableNodes = currentNode?.outgoingEdges.map(edgeId => nodes.find(n => n.id === edgeId)).filter(Boolean) || [];

  return (
    <Box className="max-w-3xl mx-auto p-6 bg-white shadow-md rounded-md">
      <Typography variant="h5" align="center" gutterBottom>
        Form Details
      </Typography>

      {message.text && <Message text={message.text} type={message.type} />}

      {!isRejected && !isApproved && !isForwarded && (
        <div>
          <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.100', borderRadius: 1 }}>
            {formData && formData.length > 0 ? (
              formData.map((field) => (
                <Typography key={field._id} variant="body1" sx={{ mb: 1 }}>
                  <strong>{field.fieldName}:</strong> {Array.isArray(field.value) ? field.value.join(', ') : field.value}
                </Typography>
              ))
            ) : (
              <Typography>No form data available.</Typography>
            )}
          </Box>

          <Typography variant="h6" align="center" gutterBottom>
            You can forward this document to:
          </Typography>

          {forwardableNodes.length > 0 ? (
            forwardableNodes.map(node => (
              <Box key={node.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
                <Typography variant="body1">{node.name}</Typography>
                <Button onClick={() => handleForward(node.id)} variant="contained" color="primary">
                  Forward
                </Button>
              </Box>
            ))
          ) : (
            <Typography align="center">No approvers available for forwarding.</Typography>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Button onClick={handleReject} variant="contained" color="error">
              Reject Document
            </Button>
          </Box>

          {finalApproverNodes.some(node => node.name === role) && !isRejected && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button
                onClick={handleApprove}
                variant="contained"
                sx={{
                  backgroundColor: 'success.main', 
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'success.dark', // Darker green on hover
                  },
                }}
              >
                Approve Document
              </Button>
            </Box>
          )}
        </div>
      )}
    </Box>
  );
};

export default PendingFormDetails;
